"use client";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ArrowRight,
  CheckCircle2,
  ChevronRight,
  Coins,
  Globe2,
  LayoutDashboard,
  Link2,
  Sparkles,
  Upload,
  Workflow,
} from "lucide-react";
import { useEffect } from "react";
import { useJsonTranslation } from "@/hooks/useJsonTranslation";
import { PublicLink as Link } from "@/components/public/PublicLink";
import {
  getActionPriceSeedAmount,
  resolveActionCharge,
} from "@/lib/constants/action-pricing";
import { STARTER_GRANT_AMOUNT } from "@/lib/constants/seeds";
import { DUBBING_LANGUAGE_COUNT, LANGUAGE_COUNT } from "@/lib/languages";
import { PLATFORM_REGISTRY } from "@/lib/platforms/registry";
import { rewriteMarketingHref } from "@/lib/seo/public-seo";
import { trackEvent } from "@/lib/analytics/amplitude";
import {
  FEATURE_DISCOVERED,
  JOURNEY_STEP_REACHED,
} from "@/lib/analytics/amplitude-events";

const PUBLISHING_PLATFORM_COUNT = Object.values(PLATFORM_REGISTRY).filter(
  (platform) => platform.publishingImplemented
).length;

const steps = [
  {
    number: 1,
    titleKey: "steps.createAccount.title",
    descriptionKey: "steps.createAccount.description",
    icon: Sparkles,
    actionKey: "steps.createAccount.action",
    href: "/signup",
  },
  {
    number: 2,
    titleKey: "steps.chooseWorkflow.title",
    descriptionKey: "steps.chooseWorkflow.description",
    icon: Workflow,
    actionKey: "steps.chooseWorkflow.action",
    href: "/how-to",
  },
  {
    number: 3,
    titleKey: "steps.connectPlatforms.title",
    descriptionKey: "steps.connectPlatforms.description",
    icon: Link2,
    actionKey: "steps.connectPlatforms.action",
    href: "/how-to",
  },
  {
    number: 4,
    titleKey: "steps.optimizePublish.title",
    descriptionKey: "steps.optimizePublish.description",
    icon: LayoutDashboard,
    actionKey: "steps.optimizePublish.action",
    href: "/dashboard",
  },
] as const;

const stats = [
  {
    titleKey: "stats.starterSeeds.title",
    descriptionKey: "stats.starterSeeds.description",
    icon: Coins,
    valueKey: "stats.starterSeeds.value",
  },
  {
    titleKey: "stats.projectCost.title",
    descriptionKey: "stats.projectCost.description",
    icon: Upload,
    valueKey: "stats.projectCost.value",
  },
  {
    titleKey: "stats.languageSupport.title",
    descriptionKey: "stats.languageSupport.description",
    icon: Globe2,
    valueKey: "stats.languageSupport.value",
  },
] as const;

const paths = [
  {
    titleKey: "paths.fullProject.title",
    descriptionKey: "paths.fullProject.description",
    actionKey: "paths.fullProject.action",
    href: "/projects/new",
    icon: Upload,
    bulletKeys: [
      "paths.fullProject.bullets.0",
      "paths.fullProject.bullets.1",
      "paths.fullProject.bullets.2",
    ],
  },
  {
    titleKey: "paths.localTools.title",
    descriptionKey: "paths.localTools.description",
    actionKey: "paths.localTools.action",
    href: "/features",
    icon: Sparkles,
    bulletKeys: [
      "paths.localTools.bullets.0",
      "paths.localTools.bullets.1",
      "paths.localTools.bullets.2",
    ],
  },
  {
    titleKey: "paths.connectedTools.title",
    descriptionKey: "paths.connectedTools.description",
    actionKey: "paths.connectedTools.action",
    href: "/how-to",
    icon: Link2,
    bulletKeys: [
      "paths.connectedTools.bullets.0",
      "paths.connectedTools.bullets.1",
      "paths.connectedTools.bullets.2",
    ],
  },
] as const;

const quickTips = [
  "quickTips.0",
  "quickTips.1",
  "quickTips.2",
  "quickTips.3",
] as const;

export default function GettingStartedPage() {
  const { t } = useJsonTranslation("GettingStartedPage");

  useEffect(() => {
    trackEvent(FEATURE_DISCOVERED, { feature: "getting_started" });
    trackEvent(JOURNEY_STEP_REACHED, { step: "landing" });
  }, []);

  const copyParams = {
    starterSeeds: String(STARTER_GRANT_AMOUNT),
    projectCost: String(getActionPriceSeedAmount("new_project")),
    translationCost: String(getActionPriceSeedAmount("translation_language")),
    dubbingCost: String(
      resolveActionCharge("dubbing", { durationSeconds: 30 }).amount
    ),
    languageCount: String(LANGUAGE_COUNT),
    dubbingLanguageCount: String(DUBBING_LANGUAGE_COUNT),
    platformCount: String(PUBLISHING_PLATFORM_COUNT),
  };

  return (
    <div className="container mx-auto px-4 py-12 pt-24 sm:py-16">
      <div className="mx-auto mb-12 max-w-4xl text-center">
        <p className="text-muted-foreground mb-4 text-sm tracking-widest uppercase">
          {t("hero.eyebrow")}
        </p>
        <h1 className="text-foreground text-wrap-balance mb-4 text-4xl font-bold sm:text-5xl">
          {t("hero.title")}
        </h1>
        <p className="text-muted-foreground mx-auto max-w-3xl text-lg">
          {t("hero.subtitle", copyParams)}
        </p>
        <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
          <Link href="/signup">
            <Button className="bg-leaf-500 hover:bg-leaf-600 min-w-44">
              {t("hero.primaryCta")} <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
          <Link href="/how-to">
            <Button variant="outline" className="min-w-44">
              {t("hero.secondaryCta")}
            </Button>
          </Link>
        </div>
      </div>

      <div className="mx-auto mb-16 grid max-w-5xl gap-4 md:grid-cols-3">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.titleKey} variant="glass">
              <CardHeader>
                <div className="bg-leaf-500/10 mb-3 flex h-10 w-10 items-center justify-center rounded-lg">
                  <Icon className="text-leaf-500 h-5 w-5" aria-hidden="true" />
                </div>
                <CardDescription>{t(stat.titleKey)}</CardDescription>
                <CardTitle className="text-3xl">
                  {t(stat.valueKey, copyParams)}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm">
                  {t(stat.descriptionKey, copyParams)}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="mx-auto mb-16 max-w-4xl">
        <div className="mb-8 text-center">
          <h2 className="text-foreground text-wrap-balance text-2xl font-bold sm:text-3xl">
            {t("stepsSection.title")}
          </h2>
          <p className="text-muted-foreground mt-3">
            {t("stepsSection.description")}
          </p>
        </div>
        <ol className="space-y-4">
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <li key={step.number} className="flex items-start gap-4">
                <div className="flex flex-col items-center">
                  <div className="bg-leaf-500 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold text-white">
                    {step.number}
                  </div>
                  {index < steps.length - 1 && (
                    <div className="bg-foreground/10 mt-1 h-full w-0.5 flex-1" />
                  )}
                </div>
                <Card variant="glass" className="mb-4 flex-1">
                  <CardHeader>
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="flex items-center gap-3">
                        <Icon
                          className="text-leaf-600 dark:text-leaf-400 h-5 w-5"
                          aria-hidden="true"
                        />
                        <CardTitle className="text-lg">
                          {t(step.titleKey)}
                        </CardTitle>
                      </div>
                      <Link href={rewriteMarketingHref(step.href)}>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-leaf-600 dark:text-leaf-400 hover:text-leaf-600 dark:hover:text-leaf-300"
                        >
                          {t(step.actionKey)}{" "}
                          <ChevronRight className="ml-1 h-4 w-4" />
                        </Button>
                      </Link>
                    </div>
                    <CardDescription className="mt-2">
                      {t(step.descriptionKey, copyParams)}
                    </CardDescription>
                  </CardHeader>
                </Card>
              </li>
            );
          })}
        </ol>
      </div>

      <div className="mx-auto mb-16 max-w-5xl">
        <div className="mb-8 text-center">
          <h2 className="text-foreground text-wrap-balance text-2xl font-bold sm:text-3xl">
            {t("pathsSection.title")}
          </h2>
          <p className="text-muted-foreground mt-3">
            {t("pathsSection.description")}
          </p>
        </div>
        <div className="grid gap-4 lg:grid-cols-3">
          {paths.map((path) => {
            const Icon = path.icon;
            return (
              <Card
                key={path.titleKey}
                variant="glass"
                className="flex h-full flex-col"
              >
                <CardHeader>
                  <div className="bg-leaf-500/10 mb-3 flex h-10 w-10 items-center justify-center rounded-lg">
                    <Icon
                      className="text-leaf-500 h-5 w-5"
                      aria-hidden="true"
                    />
                  </div>
                  <CardTitle className="text-xl">{t(path.titleKey)}</CardTitle>
                  <CardDescription>
                    {t(path.descriptionKey, copyParams)}
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex flex-1 flex-col justify-between gap-6">
                  <ul className="space-y-3">
                    {path.bulletKeys.map((bulletKey) => (
                      <li key={bulletKey} className="flex items-start gap-3">
                        <CheckCircle2 className="text-leaf-400 mt-0.5 h-5 w-5 flex-shrink-0" />
                        <span className="text-muted-foreground text-sm">
                          {t(bulletKey, copyParams)}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <Link href={rewriteMarketingHref(path.href)}>
                    <Button variant="outline" className="w-full">
                      {t(path.actionKey)}{" "}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      <div className="mx-auto mb-16 max-w-4xl">
        <Card variant="glass">
          <CardHeader>
            <CardTitle>{t("tips.title")}</CardTitle>
            <CardDescription>{t("tips.description")}</CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3">
              {quickTips.map((tipKey) => (
                <li key={tipKey} className="flex items-start gap-3">
                  <CheckCircle2 className="text-leaf-400 mt-0.5 h-5 w-5 flex-shrink-0" />
                  <span className="text-muted-foreground">
                    {t(tipKey, copyParams)}
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>

      <div className="text-center">
        <Card variant="glass" className="mx-auto max-w-3xl">
          <CardHeader>
            <CardTitle>{t("cta.title")}</CardTitle>
            <CardDescription>
              {t("cta.description", copyParams)}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3 sm:flex-row">
            <Link href="/signup" className="flex-1">
              <Button className="bg-leaf-500 hover:bg-leaf-600 w-full">
                {t("cta.primary")} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link href="/features" className="flex-1">
              <Button variant="outline" className="w-full">
                {t("cta.secondary")}
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
